<template>
  <div class="evidence-observations">
    <div v-if="positiveObservations.length" class="evidence-observations__group">
      <div class="evidence-observations__heading evidence-observations__heading--positive">
        <span class="evidence-observations__dot evidence-observations__dot--positive" />
        {{ $t('app.assessment_mode.text.input.observation_positive') }}
      </div>
      <div
          v-for="item in positiveObservations"
          :key="item.id"
          class="evidence-observations__item"
      >
        <span class="evidence-observations__label">{{ item.nameToken ? $t(item.nameToken) : formatLabel(item.name) }} <template v-if="unitToken(item)"> ({{ $t(unitToken(item)) }})</template></span>
        <span class="evidence-observations__status">
          {{ $t('app.assessment_mode.text.input.observation_reported') }}
          <span v-if="shouldShowObservationValue(item)" class="evidence-observations__status">
            {{ item.valueToken ? $t(item.valueToken) : observationValueLabel(item) }}
          </span>
          <span v-if="item.duration"> - {{ item.duration }}</span>
        </span>
      </div>
    </div>

    <div v-if="negativeObservations.length" class="evidence-observations__group">
      <div class="evidence-observations__heading evidence-observations__heading--negative">
        <span class="evidence-observations__dot evidence-observations__dot--negative" />
        {{ $t('app.assessment_mode.text.input.observation_negative') }}
      </div>
      <div
          v-for="item in negativeObservations"
          :key="item.id"
          class="evidence-observations__item"
      >
        <span class="evidence-observations__label">{{ item.nameToken ? $t(item.nameToken) : formatLabel(item.name) }}</span>
        <span class="evidence-observations__status">
          {{ $t('app.assessment_mode.text.input.observation_not_reported') }}
          <span v-if="shouldShowObservationValue(item)" class="evidence-observations__status">
            {{ item.valueToken ? $t(item.valueToken) : observationValueLabel(item) }}<template v-if="unitToken(item)"> {{ $t(unitToken(item)) }}</template>
          </span>
          <span v-if="item.duration"> - {{ item.duration }}</span>
        </span>
      </div>
    </div>

    <div v-if="unknownObservations.length" class="evidence-observations__group">
      <div class="evidence-observations__heading evidence-observations__heading--unknown">
        <span class="evidence-observations__dot evidence-observations__dot--unknown" />
        {{ $t('app.assessment_mode.text.input.option.unknown') }}
      </div>
      <div
          v-for="item in unknownObservations"
          :key="item.id"
          class="evidence-observations__item"
      >
        <span class="evidence-observations__label">{{ item.nameToken ? $t(item.nameToken) : formatLabel(item.name) }}</span>
        <span class="evidence-observations__status">
          <span v-if="shouldShowObservationValue(item)">
            {{ item.valueToken ? $t(item.valueToken) : observationValueLabel(item) }}<template v-if="unitToken(item)"> {{ $t(unitToken(item)) }}</template>
          </span>
          <span v-else>{{ $t('app.assessment_mode.text.input.option.unknown') }}</span>
          <span v-if="item.duration"> - {{ item.duration }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import type { EvidenceItem } from '../../types/text-mode.types';
import {
  observationUnitToken,
  shouldShowObservationValue,
} from '../../utils/clinical-observation-display';
import { formatLabel } from '../../utils/text-utils';

type ObservationDisplayValue = string | number | boolean | null;

const observationValueLabel = (item: EvidenceItem): ObservationDisplayValue => {
  if (item.observationType === 'option' && typeof item.value === 'string') {
    return formatLabel(item.value);
  }

  return item.value;
};

export default defineComponent({
  name: 'ClinicalObservationsList',

  props: {
    positiveObservations: {
      type: Array as PropType<EvidenceItem[]>,
      required: true,
    },
    negativeObservations: {
      type: Array as PropType<EvidenceItem[]>,
      required: true,
    },
    unknownObservations: {
      type: Array as PropType<EvidenceItem[]>,
      default: () => [],
    },
  },

  setup() {
    return {
      shouldShowObservationValue,
      observationValueLabel,
      formatLabel,
      unitToken: observationUnitToken,
    };
  },
});
</script>
